import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, HelperService, UserService } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "myp-list-item",
  templateUrl: "list-item.component.html",
  standalone: false,
})
export class ListItemComponent extends BaseComponent {
  @Input() item: any;
  @Input() showImage: boolean = true;
  @Input() photo: string;
  @Input() photoAlt: string;
  @Input() icon: IconProp;
  @Input() collapsible: boolean;
  @Input() expandedByDefault: boolean;
  @Input() title: string;
  @Input() alignTop: boolean = false;
  @Input() imageRound: boolean = true;
  @Input() imageBorder: boolean = true;
  @Input() largeImage: boolean = false;
  @Input() hoverState: boolean = true;
  @Input() includeBottomBorder: boolean = false;
  @Input() subData: Array<string>;
  @Input() subDataHtml: any;
  @Input() metadata: Array<{ title: string; value: string; redirect?: string; color?: string }>;
  @Input() includeChevron: boolean = true;
  @Input() chevronTooltip = "";
  @Input() inactive: boolean;
  @Input() showBackground: boolean = false;
  @Input() href: string = ""; // The URL to open
  @Input() marginBottom: string = "0px";
  @Input() collapsed: boolean = true;
  @Input() embeddedInCollapsible: boolean;
  @Input() showUnreadBadge: boolean;
  @Input() forceDarkMode: boolean = false;
  @Input() description: string;
  @Output() collapsedChanged: EventEmitter<{ item: any; state: "expanded" | "collapsed" }> = new EventEmitter();

  // Pagination inputs - simplified for internal management
  @Input() enablePagination: boolean = false;
  @Input() items: any[] = []; // Array of items to paginate
  @Input() itemsPerPage: number = 10;
  @Input() showPaginationInfo: boolean = true;
  @Input() paginationPosition: "top" | "bottom" = "bottom";

  @Output() itemClicked: EventEmitter<any> = new EventEmitter();
  @Output() redirectClicked: EventEmitter<void> = new EventEmitter();
  @Output() editClicked: EventEmitter<any> = new EventEmitter();
  @Output() deleteClicked: EventEmitter<any> = new EventEmitter();

  // Internal pagination state
  currentPage: number = 1;
  totalPages: number = 1;
  totalItems: number = 0;
  paginatedItems: any[] = [];
  paginationInfo: string = "";
  pageNumbers: number[] = [];

  constructor(
    userService: UserService,
    public translate: TranslateService,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.expandedByDefault) this.collapsed = false;
    if (!this.chevronTooltip) {
      this.chevronTooltip = this.translate.instant("general.view");
    }
    if (this.description) {
      this.description = this.helperService.htmlToText(this.description);
    }
    this.updatePagination();
  }

  ngOnChanges() {
    this.updatePagination();
  }

  private updatePagination(): void {
    if (!this.enablePagination || !this.items) {
      this.paginatedItems = this.items || [];
      this.paginationInfo = "";
      this.pageNumbers = [];
      return;
    }

    this.totalItems = this.items.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

    // Ensure current page is within valid range
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages || 1;
    }

    // Calculate paginated items
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedItems = this.items.slice(startIndex, endIndex);

    // Pre-compute pagination info
    if (this.showPaginationInfo && this.enablePagination) {
      const startItem = (this.currentPage - 1) * this.itemsPerPage + 1;
      const endItem = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
      this.paginationInfo = `${this.translate.instant("general.showing")} ${startItem}-${endItem} ${this.translate.instant("general.of")} ${this.totalItems} ${this.translate.instant("general.items")}`;
    } else {
      this.paginationInfo = "";
    }

    // Pre-compute page numbers
    const pages: number[] = [];
    const maxVisiblePages = 5;
    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
      if (end - start < maxVisiblePages - 1) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    this.pageNumbers = pages;
  }

  itemClick(event: any) {
    if (this.collapsible) {
      this.collapsed = !this.collapsed;
      this.collapsedChanged.emit({ item: this.item, state: this.collapsed ? "collapsed" : "expanded" });
    } else if (this.href) {
      // Allow native behavior for middle-click, ctrl+click, cmd+click (open in new tab)
      if (event.button === 1 || event.ctrlKey || event.metaKey) {
        return;
      }
      // Normal left click - prevent navigation, emit event for SPA routing
      event.preventDefault();
      if (event && event.target && event.target.className && event.target.className.includes && event.target.className.includes("circle-icon-btn")) return;

      setTimeout(
        () => {
          this.itemClicked.next(this.item);
        },
        event.key ? 100 : 1
      );
    } else {
      // Standard item click behavior
      if (event && event.target && event.target.className && event.target.className.includes && event.target.className.includes("circle-icon-btn")) return;

      setTimeout(
        () => {
          this.itemClicked.next(this.item);
        },
        event.key ? 100 : 1
      );
    }
  }

  // Button action handlers for paginated items
  onEditClick(item: any, event: any): void {
    if (event) {
      event.stopPropagation();
    }
    this.editClicked.emit(item);
  }

  onDeleteClick(item: any, event: any): void {
    if (event) {
      event.stopPropagation();
    }
    this.deleteClicked.emit(item);
  }

  onItemClick(item: any, event: any): void {
    if (this.collapsible) {
      this.collapsed = !this.collapsed;
    } else if (item.href && (event.metaKey || event.ctrlKey) && event.shiftKey) {
      // Check if the click involves cmd/ctrl and shift keys for opening in a new tab
      window.open(item.href, "_blank");
      event.preventDefault();
    } else {
      // Standard item click behavior
      if (event && event.target && event.target.className && event.target.className.includes && event.target.className.includes("circle-icon-btn")) return;

      setTimeout(
        () => {
          this.itemClicked.next(item);
        },
        event.key ? 100 : 1
      );
    }
  }

  // Pagination methods - now internal
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  getPaginationInfo(): string {
    if (!this.showPaginationInfo || !this.enablePagination) return "";

    const startItem = (this.currentPage - 1) * this.itemsPerPage + 1;
    const endItem = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);

    return `${this.translate.instant("general.showing")} ${startItem}-${endItem} ${this.translate.instant("general.of")} ${this.totalItems} ${this.translate.instant("general.items")}`;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (this.totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show a window of pages around current page
      let start = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);

      // Adjust start if we're near the end
      if (end - start < maxVisiblePages - 1) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  }
}
